'use client';

import { Columns3, Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { BulkEditColumnId } from '../bulk-edit.types';
import { getBulkEditColumnLabel, getCustomizeColumnOptions } from '../bulk-edit.utils';

interface BulkEditColumnPickerProps {
  visibleColumns: Set<BulkEditColumnId>;
  toggleColumn: (column: BulkEditColumnId) => void;
  isCreditEnabled: boolean;
}

export function BulkEditColumnPicker({
  visibleColumns,
  toggleColumn,
  isCreditEnabled,
}: BulkEditColumnPickerProps) {
  const { t } = useTranslation(['products', 'common']);

  const columnOptions = React.useMemo(
    () => getCustomizeColumnOptions(isCreditEnabled),
    [isCreditEnabled],
  );

  const hiddenCount = React.useMemo(
    () => columnOptions.filter((column) => !visibleColumns.has(column)).length,
    [columnOptions, visibleColumns],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          role="combobox"
          aria-label={t('common:dataTable.toggleColumns')}
          className="relative h-9 shrink-0 gap-1.5 text-[0.8125rem]"
        >
          <Columns3 className="size-3.5" />
          {t('products:bulkEditor.customizeColumns')}
          {hiddenCount > 0 ? (
            <span
              className="absolute right-1.5 top-1 size-1.5 rounded-full bg-primary ring-2 ring-card"
              aria-hidden
            />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="crm-view-columns w-56 p-0">
        <Command
          className={cn(
            '[&_[cmdk-group-heading]]:text-xs',
            '[&_[cmdk-input-wrapper]]:h-9 [&_[cmdk-input-wrapper]_svg]:size-3.5',
            '[&_[cmdk-input]]:h-8 [&_[cmdk-input]]:text-xs',
            '[&_[cmdk-item]]:py-2 [&_[cmdk-item]]:text-xs',
            '[&_[cmdk-empty]]:text-xs',
          )}
        >
          <CommandInput placeholder={t('common:dataTable.searchColumns')} />
          <CommandList className="max-h-64">
            <CommandEmpty>{t('common:dataTable.noColumnsFound')}</CommandEmpty>
            <CommandGroup>
              {columnOptions.map((column) => {
                const isVisible = visibleColumns.has(column);
                const label = getBulkEditColumnLabel(t, column);

                return (
                  <CommandItem
                    key={column}
                    value={`${column} ${label}`}
                    onSelect={() => toggleColumn(column)}
                    className="gap-2"
                  >
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-xs font-normal leading-none',
                        isVisible ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {label}
                    </span>
                    {isVisible ? (
                      <Eye className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    ) : (
                      <EyeOff className="size-3.5 shrink-0 text-muted-foreground/80" aria-hidden />
                    )}
                    <span className="sr-only">
                      {isVisible
                        ? t('common:dataTable.columnVisible')
                        : t('common:dataTable.columnHidden')}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
